import { DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import { ShoppingSupport } from '@inter-ikea-kompis/utilities';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisDefaultFields, TranslationsService } from '@inter-ikea-kompis/services';

import { Environment, getEnvironment } from 'utils/platform';
import { getRange } from 'utils/queryUtils';
import { getCountry, getLanguage } from 'i18n/i18nUtils';
import { IStorageOneDexfSettings, KompisServices, IStorageOneTranslations } from './types';

export const getDexfSettingsFilterSegment = () => {
  const environment = getEnvironment();
  if (environment === Environment.DEVELOPMENT) {
    return DexfSettingsFilterSegmentEnum.development;
  } else if (
    environment === Environment.VERIFICATION ||
    environment === Environment.VERIFICATION_PR
  ) {
    return DexfSettingsFilterSegmentEnum.verification;
  }
  return DexfSettingsFilterSegmentEnum.production;
};

export const getTranslationEndpoints = (countryCode: string, languageCode: string) => {
  const translationEndpoints = [
    `/addon-app/translations/kompis/en-XZ.json`,
    `/addon-app/translations/kompis/${languageCode}-${countryCode}.json`,
    // Add application specific endpoints.
    `/addon-app/storageone/translations/en-XZ.json`,
    `/addon-app/storageone/translations/${languageCode}-${countryCode}.json`
  ];

  return translationEndpoints;
};

export const getServiceInfo = (dexfSettings: IStorageOneDexfSettings): KompisServices => {
  const shoppingListSupported = ShoppingSupport.hasListSupport(dexfSettings);
  const shoppingCartSupported = ShoppingSupport.hasCartSupport(dexfSettings);
  return {
    shoppingListSupported,
    shoppingCartSupported
  };
};

export const productFields: ProductOptionalFieldNameEnum[] = [
  ...KompisDefaultFields,
  ProductOptionalFieldNameEnum.filterAttribute
];

/**
 * Phrase uses double curly brackets {{parameter}} but our formatMessage can only handle single {parameter}.
 * This function converts those keys with two curly brackets to one instead
 */
export const replaceDoubleCurlyBrackets = (obj: any): any => {
  type NestedObject = {
    [key: string]: any;
  };
  if (typeof obj === 'string') {
    return obj.replace(/{{/g, '{').replace(/}}/g, '}');
  } else if (Array.isArray(obj)) {
    return obj.map(item => replaceDoubleCurlyBrackets(item));
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = replaceDoubleCurlyBrackets(obj[key]);
      return acc;
    }, {} as NestedObject);
  }
  return obj;
};

export const getPhraseTranslations = async (locale: string): Promise<IStorageOneTranslations> => {
  const range = getRange();
  const applicationName = range.toUpperCase();
  const countryCode = getCountry(locale);
  const languageCode = getLanguage(locale);

  const settingsService = new TranslationsService<IStorageOneTranslations>({
    translationEndpoints: getTranslationEndpoints(countryCode, languageCode),
    locale: locale,
    translationPrefixApplication: applicationName
  });

  const translations = await settingsService.getTranslations();
  return translations as IStorageOneTranslations;
};
